import * as THREE from 'three'
import { TILE_SIZE, PIXEL_SIZE, ENTITY_Z_STEP, WORLD_Z_PER_Y } from '../shared/shared'

/**
 * Layer indices. Each is a quad in the single geometry.
 */
export const Layers = {
  // base
  body: 0,
  lHand: 1,
  rHand: 2,
  // gear
  legs: 3,
  chest: 4,
  head: 5,
  back: 6,
  lItem: 7,
  rItem: 8,

  // old...
  // SHADOW: 0,
  // BACK: 1,
  // LEGS: 3,
  // SHIRT: 4,
  // CHEST: 5,
  // HEAD: 6,
  // HAIR: 7,
  // HELD: 8,
}

const LAYER_COUNT = 9

export const Direction = {
  SOUTH: 'south',
  WEST: 'west',
  EAST: 'east',
  NORTH: 'north',
}

/**
 * Per-direction z ordering. Higher index = on top.
 */
const LAYER_ORDER = {
  [Direction.SOUTH]: [
    Layers.back,
    Layers.body,
    Layers.legs,
    Layers.chest,
    Layers.head,
    Layers.lItem, // for items behind the hand being held, but can also be a replacement that hides hand
    Layers.lHand,
    Layers.rItem, // for items behind the hand being held, but can also be a replacement that hides hand
    Layers.rHand,

    // old...
    // Layers.SHADOW,
    // Layers.BACK,
    // Layers.LEGS,
    // Layers.SHIRT,
    // Layers.CHEST,
    // Layers.HAIR,
    // Layers.HEAD,
    // Layers.HELD,
  ],
  [Direction.NORTH]: [
    Layers.lItem,
    Layers.lHand,
    Layers.rItem,
    Layers.rHand,
    Layers.body,
    Layers.legs,
    Layers.chest,
    Layers.back,
    Layers.head,

    // old...
    // Layers.SHADOW,
    // Layers.HELD,
    // Layers.LEGS,
    // Layers.SHIRT,
    // Layers.CHEST,
    // Layers.BACK,
    // Layers.HAIR,
    // Layers.HEAD,
  ],
  [Direction.EAST]: [
    Layers.lItem,
    Layers.lHand,
    Layers.back,
    Layers.body,
    Layers.legs,
    Layers.chest,
    Layers.head,
    Layers.rItem,
    Layers.rHand,

    // old...
    // Layers.SHADOW,
    // Layers.BACK,
    // Layers.LEGS,
    // Layers.SHIRT,
    // Layers.CHEST,
    // Layers.HAIR,
    // Layers.HEAD,
    // Layers.HELD,
  ],
  [Direction.WEST]: [
    Layers.rItem,
    Layers.rHand,
    Layers.back,
    Layers.body,
    Layers.legs,
    Layers.chest,
    Layers.head,
    Layers.lItem,
    Layers.lHand,

    // old...
    // Layers.SHADOW,
    // Layers.HELD,
    // Layers.BACK,
    // Layers.LEGS,
    // Layers.SHIRT,
    // Layers.CHEST,
    // Layers.HAIR,
    // Layers.HEAD,
  ],
}

function getLayerDepthRanks(direction) {
  const order = LAYER_ORDER[direction]
  const ranks = new Float32Array(LAYER_COUNT)
  for (let rank = 0; rank < LAYER_COUNT; rank++) {
    ranks[order[rank]] = rank
  }
  return ranks
}

// Tint channel 1: 333° magenta in Aseprite HSV, normalised to 0-1
const TINT_HUE_KEY = 333.0 / 360.0
const TINT_HUE_TOLERANCE = 5.0 / 360.0

// Tint channel 2: 180° cyan — for skin/secondary color (e.g. hands baked into equipment layers)
const TINT2_HUE_KEY = 180.0 / 360.0
const TINT2_HUE_TOLERANCE = 5.0 / 360.0

// ── shaders ──

const vertexShader = /* glsl */ `
  precision highp float;

  attribute float aLayerIndex;
  attribute float aLoaded;

  uniform float uTime;
  uniform float uCameraY;
  uniform float uWorldZPerY;
  uniform float uSortOffset;

  // locomotion (looping)
  uniform float uLocoFrom;
  uniform float uLocoTo;
  uniform float uLocoFps;
  uniform float uLocoStart;

  // emote (one-shot, -1 start = inactive)
  uniform float uEmoFrom;
  uniform float uEmoTo;
  uniform float uEmoFps;
  uniform float uEmoStart;
  uniform float uEmoLoop;

  uniform float uTotalFrames;
  uniform vec4 uAtlasRect[${LAYER_COUNT}];

  varying vec2 vUv;
  varying float vLoaded;
  varying float vLayerIndex;
  varying vec2 vWorldPos;

  void main() {
    int idx = int(aLayerIndex);
    vLoaded = aLoaded;
    vLayerIndex = aLayerIndex;

    float frame;

    // emote takes priority
    if (uEmoStart < -1.5) {
      // frozen at last frame (freezeEmote sentinel: -2)
      frame = uEmoTo;
    } else if (uEmoStart >= 0.0) {
      float emoCount = uEmoTo - uEmoFrom + 1.0;
      float emoT = (uTime - uEmoStart) * uEmoFps;
      if (emoT < emoCount) {
        frame = uEmoFrom + floor(emoT);
      } else if (uEmoLoop < 0.5) {
        // non-looping emote: freeze on last frame
        frame = uEmoTo;
      } else {
        // looping emote finished, fall back to locomotion
        float locoCount = uLocoTo - uLocoFrom + 1.0;
        float locoT = (uTime - uLocoStart) * uLocoFps;
        frame = uLocoFrom;
        if (locoCount > 1.0 && uLocoFps > 0.0) {
          frame += floor(mod(locoT, locoCount));
        }
      }
    } else {
      float locoCount = uLocoTo - uLocoFrom + 1.0;
      float locoT = (uTime - uLocoStart) * uLocoFps;
      frame = uLocoFrom;
      if (locoCount > 1.0 && uLocoFps > 0.0) {
        frame += floor(mod(locoT, locoCount));
      }
    }

    vec4 rect = uAtlasRect[idx];
    float cellU = rect.z / uTotalFrames;
    float u = rect.x + (frame + uv.x) * cellU;
    float v = rect.y + uv.y * rect.w;
    vUv = vec2(u, v);

    float pivotY = modelMatrix[3].y + uSortOffset;
    float sortZ = (uCameraY - pivotY) * uWorldZPerY;
    vec3 localPos = vec3(position.xy, position.z + sortZ);
    vec4 worldPos = modelMatrix * vec4(localPos, 1.0);
    vWorldPos = worldPos.xy;
    gl_Position = projectionMatrix * viewMatrix * worldPos;
  }
`

// Fragment shader with lighting injection point
function makeFragmentShader(lights) {
  return /* glsl */ `
    precision highp float;

    uniform sampler2D uAtlas;
    uniform vec4 uTintColor[${LAYER_COUNT}];
    uniform float uTintHueKey;
    uniform float uTintHueTolerance;
    uniform vec4 uTint2Color[${LAYER_COUNT}];
    uniform float uTint2HueKey;
    uniform float uTint2HueTolerance;

    varying vec2 vUv;
    varying float vLoaded;
    varying float vLayerIndex;
    varying vec2 vWorldPos;

    ${lights ? lights.UNIFORMS_GLSL : ''}
    ${lights ? lights.FUNCTION_GLSL : ''}

    float linear2srgb(float c) {
      return c <= 0.0031308 ? c * 12.92 : 1.055 * pow(c, 1.0 / 2.4) - 0.055;
    }
    float srgb2linear(float c) {
      return c <= 0.04045 ? c / 12.92 : pow((c + 0.055) / 1.055, 2.4);
    }
    vec3 linear2srgbV(vec3 c) {
      return vec3(linear2srgb(c.r), linear2srgb(c.g), linear2srgb(c.b));
    }
    vec3 srgb2linearV(vec3 c) {
      return vec3(srgb2linear(c.r), srgb2linear(c.g), srgb2linear(c.b));
    }

    vec3 rgb2hsv(vec3 c) {
      float cMax = max(c.r, max(c.g, c.b));
      float cMin = min(c.r, min(c.g, c.b));
      float delta = cMax - cMin;
      float h = 0.0;
      if (delta > 0.001) {
        if (cMax == c.r) h = mod((c.g - c.b) / delta, 6.0);
        else if (cMax == c.g) h = (c.b - c.r) / delta + 2.0;
        else h = (c.r - c.g) / delta + 4.0;
        h /= 6.0;
        if (h < 0.0) h += 1.0;
      }
      float s = (cMax > 0.001) ? (delta / cMax) : 0.0;
      return vec3(h, s, cMax);
    }

    void main() {
      if (vLoaded < 0.5) discard;
      vec4 c = texture2D(uAtlas, vUv);
      if (c.a < 0.5) discard;

      int idx = int(vLayerIndex);
      vec4 tint = uTintColor[idx];
      vec4 tint2 = uTint2Color[idx];

      if (tint.a > 0.5 || tint2.a > 0.5) {
        vec3 hsv = rgb2hsv(c.rgb);
        if (tint.a > 0.5) {
          float hueDist = abs(hsv.x - uTintHueKey);
          hueDist = min(hueDist, 1.0 - hueDist);
          if (hueDist < uTintHueTolerance && hsv.y > 0.15) {
            c.rgb = linear2srgbV(tint.rgb) * hsv.z;
          }
        }
        if (tint2.a > 0.5) {
          float hueDist = abs(hsv.x - uTint2HueKey);
          hueDist = min(hueDist, 1.0 - hueDist);
          if (hueDist < uTint2HueTolerance && hsv.y > 0.15) {
            c.rgb = linear2srgbV(tint2.rgb) * hsv.z;
          }
        }
      }

      ${lights ? 'c.rgb = applyLighting(c.rgb, vWorldPos);' : ''}

      gl_FragColor = c;
    }
  `
}

/**
 * Avatar
 * ─────────
 * Multi-layer avatar rendered in 1 draw call.
 * Sizes specified in tiles (e.g. width=2, height=2 for a 32x32 avatar).
 * Origin is bottom-center with optional offset.
 * Uses camera-relative world-depth y-sorting alongside other objects.
 *
 *   const avatar = new Avatar(scene, timeUniform, lights, {
 *     yBase: 4 * PIXEL_SIZE,  // world units (tiles)
 *     width: 2,          // tiles
 *     height: 2,         // tiles
 *     totalFrames: 22,
 *     animations: { ... },
 *   })
 *
 *   char.setLayer('body', 'human.png', '#ff3333')
 *   char.setDirection('south')
 *   char.setLocomotion('idle')
 *   char.playEmote('attack')
 *   char.setPosition(5, 3)
 */
export class Avatar {
  /**
   * @param {THREE.Scene} scene
   * @param {{ value: number }} timeUniform  shared time uniform
   * @param {Lights|null} lights  lighting system (null = no lighting)
   * @param {object} opts
   * @param {object} opts.yBase  sort offset from bottom-center
   * @param {number} opts.width       frame width in tiles (default 1)
   * @param {number} opts.height      frame height in tiles (default 1)
   * @param {number} opts.totalFrames total columns in each spritesheet
   * @param {object} opts.animations  { name: { direction: { from, to, fps } } }
   */
  constructor(
    world,
    timeUniform,
    lights,
    { yBase = 0, width = 1, height = 1, totalFrames = 1, animations = {}, shadow } = {}
  ) {
    this.world = world
    this.scene = world.scene
    this.timeUniform = timeUniform
    this.lights = lights

    // offset in world units
    this.yBase = yBase

    // sizes in tiles
    this.widthTiles = width
    this.heightTiles = height

    // sizes in pixels
    this.widthPx = width * TILE_SIZE
    this.heightPx = height * TILE_SIZE

    // world-unit dimensions
    const worldW = width
    const worldH = height

    this.totalFrames = totalFrames
    this.direction = Direction.SOUTH
    this.animations = animations
    this._locoName = null
    this._emoName = null
    this.x = 0
    this.y = 0

    // ── atlas canvas ──
    this.atlasW = totalFrames * this.widthPx
    this.atlasH = LAYER_COUNT * this.heightPx

    this.canvas = document.createElement('canvas')
    this.canvas.width = this.atlasW
    this.canvas.height = this.atlasH
    this.ctx = this.canvas.getContext('2d')
    this.ctx.imageSmoothingEnabled = false

    this.texture = new THREE.CanvasTexture(this.canvas)
    this.texture.magFilter = THREE.NearestFilter
    this.texture.minFilter = THREE.NearestFilter
    this.texture.generateMipmaps = false
    this.texture.colorSpace = THREE.LinearSRGBColorSpace

    // ── per-layer state ──
    this._layers = Array.from({ length: LAYER_COUNT }, () => ({
      url: null,
      loaded: false,
    }))

    // ── tint colors (vec4: rgb + a=1 active, a=0 inactive) ──
    this._tintColors = Array.from({ length: LAYER_COUNT }, () => new THREE.Vector4(0, 0, 0, 0))
    this._tint2Colors = Array.from({ length: LAYER_COUNT }, () => new THREE.Vector4(0, 0, 0, 0))

    // ── atlas rects ──
    const rowUV = 1 / LAYER_COUNT
    this._atlasRects = Array.from({ length: LAYER_COUNT }, (_, i) => {
      return new THREE.Vector4(0, 1 - (i + 1) * rowUV, 1, rowUV)
    })

    // ── geometry (bottom-center origin) ──
    this._buildGeometry(worldW, worldH)

    // ── material ──
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uAtlas: { value: this.texture },
        uTime: timeUniform,
        uCameraY: world.uniforms.uCameraY,
        uWorldZPerY: { value: WORLD_Z_PER_Y },
        uSortOffset: { value: yBase },
        uTotalFrames: { value: totalFrames },
        uAtlasRect: { value: this._atlasRects },
        uLocoFrom: { value: 0 },
        uLocoTo: { value: 0 },
        uLocoFps: { value: 0 },
        uLocoStart: { value: 0 },
        uEmoFrom: { value: 0 },
        uEmoTo: { value: 0 },
        uEmoFps: { value: 0 },
        uEmoStart: { value: -1 },
        uEmoLoop: { value: 1 },
        uTintColor: { value: this._tintColors },
        uTintHueKey: { value: TINT_HUE_KEY },
        uTintHueTolerance: { value: TINT_HUE_TOLERANCE },
        uTint2Color: { value: this._tint2Colors },
        uTint2HueKey: { value: TINT2_HUE_KEY },
        uTint2HueTolerance: { value: TINT2_HUE_TOLERANCE },
        ...(lights ? lights.uniforms : {}),
      },
      vertexShader,
      fragmentShader: makeFragmentShader(lights),
      transparent: false,
      depthWrite: true,
      depthTest: true,
      side: THREE.DoubleSide,
    })

    this.mesh = new THREE.Mesh(this.geometry, this.material)
    this.mesh.frustumCulled = true
    this.scene.add(this.mesh)

    this._shadow = world.shadows.add({
      url: shadow ?? '/assets/avatar.shadow.png',
      width,
      height,
    })
  }

  // ── public API ──

  setLayers(layers) {
    for (let key in Layers) {
      const value = layers[key]
      this.setLayer(key, value?.url, value?.tint, value?.tint2)
    }
  }

  setLayer(name, url, tint, tint2) {
    const idx = Layers[name]
    if (idx === undefined) {
      console.warn(`Avatar: unknown layer "${name}"`)
      return
    }
    const layer = this._layers[idx]
    layer.url = url
    if (tint !== undefined) this._setTintColor(idx, tint)
    if (tint2 !== undefined) this._setTint2Color(idx, tint2)
    if (!url) {
      layer.loaded = false
      this._setLoaded(idx, false)
      this.ctx.clearRect(0, idx * this.heightPx, this.atlasW, this.heightPx)
      this.texture.needsUpdate = true
      return
    }
    // If already in sync cache, blit immediately — no flash
    const cached = this.world.loader.get(url)
    if (cached) {
      this._blit(idx, cached)
      layer.loaded = true
      this._setLoaded(idx, true)
      return
    }
    // Not yet loaded — hide until ready
    layer.loaded = false
    this._setLoaded(idx, false)
    this.world.loader.load(url).then(img => {
      if (layer.url !== url) return
      this._blit(idx, img)
      layer.loaded = true
      this._setLoaded(idx, true)
    })
  }

  clearLayer(name) {
    this.setLayer(name, null)
  }

  setTint(name, hex) {
    const idx = Layers[name]
    if (idx === undefined) {
      console.warn(`Avatar: unknown layer "${name}"`)
      return
    }
    this._setTintColor(idx, hex)
  }

  setLocomotion(name) {
    if (this._locoName === name) return
    this._locoName = name
    this._applyLocomotion()
  }

  getLocomotion() {
    return this._locoName
  }

  playEmote(name) {
    if (!name) {
      this._emoName = null
      this.material.uniforms.uEmoStart.value = -1
      return
    }
    this._emoName = name
    this._applyEmote()
  }

  // Start an emote already at its last frame — no playback, instant freeze.
  freezeEmote(name) {
    const clip = this._resolve(name)
    if (!clip) return
    this._emoName = name
    const u = this.material.uniforms
    u.uEmoFrom.value = clip.from
    u.uEmoTo.value = clip.to
    u.uEmoFps.value = clip.fps
    u.uEmoStart.value = -2
    u.uEmoLoop.value = 0
  }

  getEmote() {
    return this._emoName
  }

  isEmotePlaying() {
    if (!this._emoName) return false
    const u = this.material.uniforms
    if (u.uEmoStart.value < -1.5) return true // frozen at last frame
    if (u.uEmoStart.value < 0) return false
    const clip = this._resolve(this._emoName)
    if (clip && clip.loop === false) return true // frozen = still "playing"
    const count = u.uEmoTo.value - u.uEmoFrom.value + 1
    const elapsed = (this.timeUniform.value - u.uEmoStart.value) * u.uEmoFps.value
    return elapsed < count
  }

  setDirection(dir) {
    if (!dir) return
    const d = typeof dir === 'string' ? dir.toLowerCase() : dir
    if (this.direction === d) return
    this.direction = d
    this._reorderZ()
    if (this._locoName) this._applyLocomotion()
    if (this.isEmotePlaying()) this._applyEmote(true)
  }

  getDirection() {
    return this.direction
  }

  setPosition(x, y) {
    this.x = x
    this.y = y
    this.mesh.position.set(x, y, 0)
    this._shadow?.setPosition(x, y)
  }

  destroy() {
    this.scene.remove(this.mesh)
    this.geometry.dispose()
    this.material.dispose()
    this.texture.dispose()
    this.canvas.width = 0
    this.canvas.height = 0
    this._shadow?.destroy()
  }

  // ── internals ──

  _setTintColor(idx, hex) {
    const v = this._tintColors[idx]
    if (!hex) {
      v.set(0, 0, 0, 0)
      return
    }
    const c = new THREE.Color(hex)
    v.set(c.r, c.g, c.b, 1.0)
  }

  _setTint2Color(idx, hex) {
    const v = this._tint2Colors[idx]
    if (!hex) {
      v.set(0, 0, 0, 0)
      return
    }
    const c = new THREE.Color(hex)
    v.set(c.r, c.g, c.b, 1.0)
  }

  _resolve(name) {
    const group = this.animations[name]
    if (!group) {
      console.warn(`Avatar: unknown animation "${name}"`)
      return null
    }
    if (group[this.direction]) return group[this.direction]
    if (group.all) return group.all
    if (group[Direction.SOUTH]) return group[Direction.SOUTH]
    if (group.from !== undefined) return group
    console.warn(`Avatar: animation "${name}" has no clip for direction "${this.direction}"`)
    return null
  }

  _applyLocomotion() {
    const clip = this._resolve(this._locoName)
    if (!clip) return
    const u = this.material.uniforms
    u.uLocoFrom.value = clip.from
    u.uLocoTo.value = clip.to
    u.uLocoFps.value = clip.fps
    u.uLocoStart.value = this.timeUniform.value
  }

  _applyEmote(preserveProgress = false) {
    const clip = this._resolve(this._emoName)
    if (!clip) return
    const u = this.material.uniforms
    if (preserveProgress && u.uEmoStart.value >= 0) {
      const elapsed = (this.timeUniform.value - u.uEmoStart.value) * u.uEmoFps.value
      u.uEmoFrom.value = clip.from
      u.uEmoTo.value = clip.to
      u.uEmoFps.value = clip.fps
      u.uEmoStart.value = this.timeUniform.value - elapsed / clip.fps
      u.uEmoLoop.value = clip.loop === false ? 0 : 1
    } else {
      u.uEmoFrom.value = clip.from
      u.uEmoTo.value = clip.to
      u.uEmoFps.value = clip.fps
      u.uEmoStart.value = this.timeUniform.value
      u.uEmoLoop.value = clip.loop === false ? 0 : 1
    }
  }

  _buildGeometry(w, h) {
    const hw = w / 2
    const verts = LAYER_COUNT * 4
    const tris = LAYER_COUNT * 6

    const pos = new Float32Array(verts * 3)
    const uv = new Float32Array(verts * 2)
    const layerIdx = new Float32Array(verts)
    const loaded = new Float32Array(verts)
    const index = new Uint16Array(tris)

    const order = LAYER_ORDER[this.direction]
    const ranks = getLayerDepthRanks(this.direction)

    // lay out vertex data sequentially by layer index (0-8)
    for (let i = 0; i < LAYER_COUNT; i++) {
      const v = i * 4
      const z = ranks[i] * ENTITY_Z_STEP

      pos[v * 3] = -hw
      pos[v * 3 + 1] = 0
      pos[v * 3 + 2] = z
      pos[(v + 1) * 3] = hw
      pos[(v + 1) * 3 + 1] = 0
      pos[(v + 1) * 3 + 2] = z
      pos[(v + 2) * 3] = -hw
      pos[(v + 2) * 3 + 1] = h
      pos[(v + 2) * 3 + 2] = z
      pos[(v + 3) * 3] = hw
      pos[(v + 3) * 3 + 1] = h
      pos[(v + 3) * 3 + 2] = z

      uv[v * 2] = 0
      uv[v * 2 + 1] = 0
      uv[(v + 1) * 2] = 1
      uv[(v + 1) * 2 + 1] = 0
      uv[(v + 2) * 2] = 0
      uv[(v + 2) * 2 + 1] = 1
      uv[(v + 3) * 2] = 1
      uv[(v + 3) * 2 + 1] = 1

      layerIdx[v] = layerIdx[v + 1] = layerIdx[v + 2] = layerIdx[v + 3] = i
      loaded[v] = loaded[v + 1] = loaded[v + 2] = loaded[v + 3] = 0
    }

    // index buffer ordered back-to-front using LAYER_ORDER
    for (let i = 0; i < LAYER_COUNT; i++) {
      const layer = order[i]
      const sv = layer * 4
      const t = i * 6
      index[t] = sv
      index[t + 1] = sv + 1
      index[t + 2] = sv + 2
      index[t + 3] = sv + 1
      index[t + 4] = sv + 3
      index[t + 5] = sv + 2
    }

    this.geometry = new THREE.BufferGeometry()
    this._posAttr = new THREE.BufferAttribute(pos, 3)
    this._loadedAttr = new THREE.BufferAttribute(loaded, 1)
    this.geometry.setAttribute('position', this._posAttr)
    this.geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2))
    this.geometry.setAttribute('aLayerIndex', new THREE.BufferAttribute(layerIdx, 1))
    this.geometry.setAttribute('aLoaded', this._loadedAttr)
    this.geometry.setIndex(new THREE.BufferAttribute(index, 1))
  }

  _reorderZ() {
    const order = LAYER_ORDER[this.direction]
    const ranks = getLayerDepthRanks(this.direction)
    const posArr = this._posAttr.array
    const idxArr = this.geometry.index.array

    for (let i = 0; i < LAYER_COUNT; i++) {
      const z = ranks[i] * ENTITY_Z_STEP
      const v = i * 4
      posArr[v * 3 + 2] = z
      posArr[(v + 1) * 3 + 2] = z
      posArr[(v + 2) * 3 + 2] = z
      posArr[(v + 3) * 3 + 2] = z

      // reorder index buffer: layer i draws layerOrder[i]
      const layer = order[i]
      const sv = layer * 4
      const t = i * 6
      idxArr[t] = sv
      idxArr[t + 1] = sv + 1
      idxArr[t + 2] = sv + 2
      idxArr[t + 3] = sv + 1
      idxArr[t + 4] = sv + 3
      idxArr[t + 5] = sv + 2
    }

    this._posAttr.needsUpdate = true
    this.geometry.index.needsUpdate = true
  }

  _setLoaded(idx, on) {
    const val = on ? 1 : 0
    const v = idx * 4
    const arr = this._loadedAttr.array
    arr[v] = arr[v + 1] = arr[v + 2] = arr[v + 3] = val
    this._loadedAttr.needsUpdate = true
  }

  _blit(idx, img) {
    const dy = idx * this.heightPx
    this.ctx.clearRect(0, dy, this.atlasW, this.heightPx)
    this.ctx.drawImage(img, 0, 0, img.width, img.height, 0, dy, img.width, img.height)
    this.texture.needsUpdate = true
  }
}
